// AppProviders.jsx
// Root provider component that combines all contexts in the correct order
import React from 'react';
import { AuthProvider } from './AuthContext';
import { ConversationsProvider } from './ConversationsContext';
import { ChatSessionProvider } from './ChatSessionContext';
import { FileUploadProvider } from './FileUploadContext';

/**
 * AppProviders wraps the application with all necessary context providers
 * in the correct dependency order:
 * 1. AuthProvider (no dependencies)
 * 2. ConversationsProvider (depends on Auth)
 * 3. ChatSessionProvider (depends on Auth and Conversations)
 * 4. FileUploadProvider (depends on Auth and ChatSession)
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @param {Object} props.user - User object from Amplify Auth
 * @param {Function} props.signOut - Sign out function from Amplify Auth
 */
export const AppProviders = ({ children, user, signOut }) => {
    return (
        <AuthProvider user={user} signOut={signOut}>
            <ConversationsProvider>
                <ChatSessionProvider>
                    <FileUploadProvider>
                        {children}
                    </FileUploadProvider>
                </ChatSessionProvider>
            </ConversationsProvider>
        </AuthProvider>
    );
};

// Export all hooks for convenience
export { useAuth } from './AuthContext';
export { useConversations } from './ConversationsContext';
export { useChatSession } from './ChatSessionContext';
export { useFileUpload } from './FileUploadContext';